import type {
  TTrajectory,
  TTrajectoryMeasure,
  TTrajectoryProjected2dPosition,
  TTrajectoryStation,
} from '../../types/trajectory';
import type { TFactTrajectoryPoint } from '../model';

function measureValue(measure: TTrajectoryMeasure | undefined | null): number | null {
  if (!measure || typeof measure.value !== 'number' || Number.isNaN(measure.value)) {
    return null;
  }

  return measure.value;
}

function firstNumber(...values: Array<number | undefined | null>): number | null {
  for (const value of values) {
    if (typeof value === 'number') {
      return value;
    }
  }

  return null;
}

function readProjectedCrsKey(projected: TTrajectoryProjected2dPosition | undefined): string | null {
  const crs = projected?.projectedCrs;

  if (!crs) {
    return null;
  }

  if (typeof crs.id === 'number') {
    return `id:${crs.id}`;
  }

  if (typeof crs.abstractGeographic2dCrsEpsgCode === 'string' && crs.abstractGeographic2dCrsEpsgCode.trim()) {
    return `epsg:${crs.abstractGeographic2dCrsEpsgCode.trim()}`;
  }

  if (typeof crs.citationTitle === 'string' && crs.citationTitle.trim()) {
    return `title:${crs.citationTitle.trim()}`;
  }

  return null;
}

export function mapApiStationToFactPoint(station: TTrajectoryStation): TFactTrajectoryPoint {
  const projected = station.location?.[0]?.projected2dPosition;

  return {
    stationId: station.id,
    md: measureValue(station.md?.measuredDepth),
    inc: measureValue(station.incl),
    azi: measureValue(station.azi),
    azMag: measureValue(station.computed?.aziMag) ?? measureValue(station.azi),
    azTrue: measureValue(station.computed?.aziTrue),
    aziGrid: measureValue(station.computed?.aziGrid),
    tvd: measureValue(station.tvd?.verticalDepth),
    tvdss: measureValue(station.computed?.tvdss),
    ns: measureValue(station.dispNs),
    we: measureValue(station.dispEw),
    northing: firstNumber(projected?.northing, projected?.coordinate1),
    easting: firstNumber(projected?.easting, projected?.coordinate2),
    projectedCrsKey: readProjectedCrsKey(projected),
    latitude: measureValue(station.location?.[0]?.geographic2dPosition?.latitude),
    longitude: measureValue(station.location?.[0]?.geographic2dPosition?.longitude),
    closure: measureValue(station.closure),
    closureDirection: measureValue(station.closureDirection),
    vsec: measureValue(station.vertSect),
    ahd: measureValue(station.computed?.ahd),
    ddi: measureValue(station.computed?.ddi),
    erd: measureValue(station.computed?.erd),
    tortuosity: measureValue(station.computed?.tortuosity),
    upToPlan: measureValue(station.computed?.upToPlan),
    rightToPlan: measureValue(station.computed?.rightToPlan),
    centrToCentr: measureValue(station.computed?.centrToCentr),
    dls: measureValue(station.dls),
    rateTurn: measureValue(station.rateTurn),
    rateBuild: measureValue(station.rateBuild),
    mdDelta: measureValue(station.mdDelta),
    tvdDelta: measureValue(station.tvdDelta),
    tfo: measureValue(station.computed?.tfo),
    mtf: measureValue(station.mtf),
    gtf: measureValue(station.gtf),
    comment: station.app?.comment ?? null,
    typeTrajStation: station.typeTrajStation ?? null,
  };
}

export function omitTrajectoryStations(trajectory: TTrajectory): Omit<TTrajectory, 'trajectoryStations'> {
  const { trajectoryStations: _, ...data } = trajectory;

  return data;
}
